// Speed 2026-10-15: the Featured slider's custom element used to be printed inline by every rail section (6.6 KB
// per copy). It now loads once from this file (deferred), so it defines the element at DOMContentLoaded; the
// connectedCallback already waits for the nav (last child) before it initialises. Code unchanged otherwise.
   (function () {
    if (customElements.get('featured-collection-slider')) return;

    class FeaturedCollectionSlider extends HTMLElement {
      connectedCallback() {
        // When a second instance of this section exists on the page, the element
        // is already defined by the time the parser reaches its opening tag, so
        // it upgrades (and fires connectedCallback) before its children exist.
        // Wait for the nav — the last child — before touching anything.
        if (this.querySelector('.fcs__nav')) {
          this.init();
          return;
        }
        this._mo = new MutationObserver(() => {
          if (!this.querySelector('.fcs__nav')) return;
          this._mo.disconnect();
          this._mo = null;
          this.init();
        });
        this._mo.observe(this, { childList: true, subtree: true });
      }

      init() {
        if (this._inited) return;
        this.track = this.querySelector('.fcs__track');
        this.prevBtn = this.querySelector('.fcs__arrow--prev');
        this.nextBtn = this.querySelector('.fcs__arrow--next');
        this.dotsWrap = this.querySelector('.fcs__dots');
        if (!this.track || !this.prevBtn || !this.nextBtn || !this.dotsWrap) return;
        this.slides = Array.from(this.track.querySelectorAll('.fcs__slide'));
        if (!this.slides.length) return;
        this._inited = true;

        this.prevBtn.addEventListener('click', () => this.scrollByPage(-1));
        this.nextBtn.addEventListener('click', () => this.scrollByPage(1));
        this.track.addEventListener('scroll', () => {
          clearTimeout(this._t);
          this._t = setTimeout(() => this.update(), 60);
        }, { passive: true });

        this._ro = new ResizeObserver(() => this.build());
        this._ro.observe(this.track);
        this.build();
      }

      disconnectedCallback() {
        if (this._ro) this._ro.disconnect();
        if (this._mo) this._mo.disconnect();
        clearTimeout(this._t);
        clearTimeout(this._retry);
      }

      // Returns false when the track has no usable layout yet, so callers can
      // retry rather than divide by a zero step and land on pageCount === 1.
      measure() {
        const a = this.slides[0];
        const b = this.slides[1] || a;
        const trackWidth = this.track.clientWidth;
        // Fractional (offsetLeft rounds to whole pixels; a 270.4px pitch rounded to 270 left the last page 2px short
        // of the track's end, so the next arrow never switched off).
        const step = (b.getBoundingClientRect().left - a.getBoundingClientRect().left) || a.getBoundingClientRect().width;
        if (!(step > 0) || !(trackWidth > 0)) return false;

        this.step = step;
        this.perView = Math.max(1, Math.round(trackWidth / step));
        this.pageCount = Math.max(1, Math.ceil(this.slides.length / this.perView));
        // Repair round 2 (2026-10-14): the last page used to scroll to the track's true end, which left a sliced
        // card at the left gutter (7 cards, 5 in view: card 3 cut at x -167). Pad the end of the track by the
        // part of a page the cards do not fill, so the last page can start on a whole card like every other page.
        // The space is a flex spacer (::after, see --fcs-trailing) rather than padding: the cards are sized
        // as a percentage of the track's inner width, which padding would shrink.
        const trailing = Math.max(0, trackWidth - this.perView * step).toFixed(2) + 'px';
        if (this.style.getPropertyValue('--fcs-trailing') !== trailing) this.style.setProperty('--fcs-trailing', trailing);
        return true;
      }

      build() {
        // Phone grid / stack mode lays the track out as a CSS grid (no sideways scroll): nothing to page
        // through, so skip the dots. The ResizeObserver re-runs this when the viewport grows into slider mode.
        if (getComputedStyle(this.track).display !== 'flex') {
          this.dotsWrap.innerHTML = '';
          this.dots = [];
          this.classList.add('fcs--single');
          this.style.removeProperty('--fcs-trailing');
          return;
        }
        if (!this.measure()) {
          clearTimeout(this._retry);
          this._retry = setTimeout(() => this.build(), 100);
          return;
        }
        this.dotsWrap.innerHTML = '';
        this.dots = [];
        for (let i = 0; i < this.pageCount; i++) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'fcs__dot';
          dot.setAttribute('aria-label', 'Go to page ' + (i + 1));
          dot.addEventListener('click', () => this.goToPage(i));
          this.dotsWrap.appendChild(dot);
          this.dots.push(dot);
        }
        this.classList.toggle('fcs--single', this.pageCount <= 1);
        this.update();
      }

      currentPage() {
        const pageWidth = this.step * this.perView;
        if (!pageWidth) return 0;
        // Because the track shows a partial "peek" of the next card, the real
        // maximum scroll is smaller than (pageCount - 1) * pageWidth. When the
        // track is scrolled to (or near) its end, always report the last page
        // so the clamped scroll position still activates the correct dot.
        const max = this.track.scrollWidth - this.track.clientWidth;
        if (max > 0 && this.track.scrollLeft >= max - 1) return this.pageCount - 1;
        return Math.min(this.pageCount - 1, Math.round(this.track.scrollLeft / pageWidth));
      }

      goToPage(index) {
        const target = Math.min(this.pageCount - 1, Math.max(0, index));
        const max = this.track.scrollWidth - this.track.clientWidth;
        // Last page: start on the first card of the last full page (whole cards from the gutter); the trailing
        // padding set in measure() makes that position reachable, and max is only a safety net.
        const lastStart = Math.max(0, this.slides.length - this.perView) * this.step;
        const left = target >= this.pageCount - 1 ? Math.min(lastStart, max) : Math.min(target * this.step * this.perView, max);
        this.track.scrollTo({ left: left, behavior: 'smooth' });
      }

      scrollByPage(dir) {
        this.goToPage(this.currentPage() + dir);
      }

      update() {
        if (!this.dots) return;
        const page = this.currentPage();
        this.dots.forEach((d, i) => d.classList.toggle('is-active', i === page));
        const max = this.track.scrollWidth - this.track.clientWidth;
        this.prevBtn.disabled = this.track.scrollLeft <= 1;
        this.nextBtn.disabled = this.track.scrollLeft >= max - 2;
      }
    }

    customElements.define('featured-collection-slider', FeaturedCollectionSlider);
  })();
